import { useSyncExternalStore } from 'react'

import type { CustomerVerification } from '@/api/types'

/**
 * Held verifications, client-side.
 *
 * `POST /customers/{id}/verifications` is valid for fifteen minutes so one counter visit can carry
 * a deposit, a withdrawal and a transfer without re-presenting the customer for each — but nothing
 * on the server hands that id back to the client on its own. Something on this side has to remember
 * it between the moment the teller verifies and the moment they press Post, and again for the next
 * posting in the same visit if there is one.
 *
 * A closure rather than a query-cache entry or React state, for the same reason `session.ts` keeps
 * the access token in one: the *screens* that read this are the withdrawal, transfer and deposit
 * forms, which `savings-cash.tsx` remounts by `key` on every tab change specifically so a half-typed
 * amount from one form never bleeds into another — see that route's own comment. A verification held
 * in one of those forms' local state would vanish the moment the teller switched from Withdrawal to
 * Transfer, which is exactly the case this exists to survive. It is never written to storage: a
 * verification is evidence a teller looked at someone moments ago, and surviving a reload would make
 * that untrue.
 *
 * Expiry is not pushed — nothing here runs a timer — it is checked on every read, against the
 * server's own `expiresAtUtc`. A component that wants to show a live countdown re-renders itself on
 * its own interval and asks again; see `VerificationStatus`.
 */

type Listener = () => void

const held = new Map<string, CustomerVerification>()
const listeners = new Set<Listener>()

function notify(): void {
  listeners.forEach((listener) => listener())
}

function isLive(verification: CustomerVerification, now: number): boolean {
  return Date.parse(verification.expiresAtUtc) > now
}

export const verificationStore = {
  /** The live verification held for this customer, or null if none exists or it has lapsed. */
  get(customerId: string): CustomerVerification | null {
    const entry = held.get(customerId)
    if (!entry) {
      return null
    }

    if (!isLive(entry, Date.now())) {
      held.delete(customerId)
      return null
    }

    return entry
  },

  hold(verification: CustomerVerification): void {
    held.set(verification.customerId, verification)
    notify()
  },

  /** Forces re-verification — used after a posting refuses the held id as expired or mismatched. */
  clear(customerId: string): void {
    if (held.delete(customerId)) {
      notify()
    }
  },

  // An arrow-function property rather than a method: `useSyncExternalStore` below takes this
  // reference unbound, and a method-shorthand function here would carry `this` semantics that
  // reference does not preserve.
  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
}

/** The live verification held for one customer, reactive to `hold`/`clear` from anywhere. */
export function useHeldVerification(customerId: string): CustomerVerification | null {
  return useSyncExternalStore(
    verificationStore.subscribe,
    () => verificationStore.get(customerId),
    () => null,
  )
}
